<script setup>
import useDates from "@/composables/useDates";
import {computed, ref} from "vue";
import SquareIconButton from "@/components/global-themed/buttons/SquareIconButton.vue";
import {useStore} from "vuex";
import MatchingKeywordsModal from "@/components/competition/partials/MatchingKeywordsModal.vue";
import CompetitorLogo from "@/components/competition/partials/CompetitorLogo.vue";

const props = defineProps({
  competitor: {
    required: true,
    type: Object,
  }
})

const showMatchingKeywords = ref(false);

const store = useStore();

const dates = useDates();

const isRecommended = computed(() => props.competitor.id === undefined)

const loading = ref(false);

const addCompetitor = () => {
  loading.value = true;
  store.dispatch('competitionV2/addCompetitor', props.competitor.simpleUrl)
    .then(() => {
      loading.value = false;
    })
    .catch(() => {
      loading.value = false;
    })
}

const removeCompetitor = () => {
  loading.value = true;
  store.dispatch('competitionV2/removeCompetitor', props.competitor.id)
    .then(() => {
      loading.value = false;
    })
    .catch(() => {
      loading.value = false;
    })
}

</script>

<template>
  <div
    class="tw-flex tw-w-full"
  >
    <div
      class="tw-bg-blue-highlight tw-flex tw-items-center tw-justify-center tw-p-6 tw-border-blue-300 tw-border tw-rounded-tl-lg tw-rounded-bl-lg tw-flex-grow-0"
    >
      <CompetitorLogo
        :competitor="competitor"
        class="tw-h-10 tw-w-10"
      />
    </div>
    <div class="tw-border tw-border-l-0 tw-rounded-tr-lg tw-rounded-br-lg tw-flex-1 tw-flex tw-items-center">
      <div class="tw-p-4 tw-w-full">
        <p
          class="tw-text-lg tw-mb-2 tw-truncate tw-max-w-[10rem] tw-text-grey-dark"
          :title="competitor.simpleUrl"
        >
          {{ competitor.simpleUrl }}
        </p>
        <p
          v-if="competitor.lastAuditedAt"
          class="tw-text-grey tw-text-xs"
        >
          {{ `Last Audited: ${dates.formattedDate(competitor.lastAuditedAt)}` }}
        </p>
        <div
          v-if="competitor.count"
          class="tw-flex tw-items-center tw-gap-1 tw-text-grey tw-text-xs tw-cursor-pointer"
          role="button"
          @click="showMatchingKeywords = true"
        >
          <svg-vue
            icon="icons/tags"
            class="tw-w-5 tw-h-5"
          />
          <span>
            {{ `${competitor.count} Matching ${competitor.count === '1' ? 'Keyword' : 'Keywords'}` }}
          </span>
        </div>
      </div>
      <div class="tw-p-4 tw-flex-0">
        <SquareIconButton
          v-if="isRecommended"
          variant="primary-inverted"
          size="small"
          :disabled="loading"
          @click.prevent.stop="addCompetitor"
        >
          <svg-vue
            v-if="loading"
            icon="icons/loading"
            class="tw-w-6 tw-h-6 tw-text-primary"
          />
          <svg-vue
            v-else
            icon="icons/plus"
            class="tw-w-6 tw-h-6 tw-text-primary"
          />
        </SquareIconButton>
        <SquareIconButton
          v-else
          variant="primary-inverted"
          size="small"
          :disabled="loading"
          @click.prevent.stop="removeCompetitor"
        >
          <svg-vue
            v-if="loading"
            icon="icons/loading"
            class="tw-w-6 tw-h-6 tw-text-primary"
          />
          <svg-vue
            v-else
            icon="icons/trash"
            class="tw-w-6 tw-h-6 tw-text-primary"
          />
        </SquareIconButton>
      </div>
    </div>
  </div>
  <Teleport to=".tailwind">
    <MatchingKeywordsModal
      v-if="showMatchingKeywords"
      :competitor="competitor"
      @close="showMatchingKeywords = false"
    />
  </Teleport>
</template>
